import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePaystackPayment } from 'react-paystack';
import { useBooking } from '../../context/BookingContext';
import { bookingService } from '../../services/booking';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

type BookingStatus = 'idle' | 'processing' | 'confirming' | 'success' | 'error';

export default function Payment() {
    const location = useLocation();
    const navigate = useNavigate();
    const { clearCart, formatPrice } = useBooking();

    const { passengers, contactEmail, contactPhone, totalAmount, cartItems } = location.state || {};

    const [status, setStatus] = useState<BookingStatus>('idle');
    const [errorMessage, setErrorMessage] = useState('');
    const [bookingResult, setBookingResult] = useState<any>(null);

    useEffect(() => {
        if (!passengers || !contactEmail || !cartItems) {
            navigate('/booking/checkout');
        }
    }, [passengers, contactEmail, cartItems, navigate]);

    // Paystack Config
    const config = {
        reference: `RIG_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
        email: contactEmail,
        amount: Math.round(totalAmount * 100), // Paystack expects kobo
        publicKey: import.meta.env.VITE_PAYSTACK_PUBLIC_KEY || '',
        currency: 'NGN',
        metadata: {
            custom_fields: [
                { display_name: "Service Type", variable_name: "service_type", value: cartItems?.[0]?.type || 'flight' },
                { display_name: "Passengers", variable_name: "passenger_count", value: passengers?.length || 1 }
            ]
        }
    };

    const onSuccess = async (reference: any) => {
        setStatus('confirming');

        try {
            // For each cart item, create a booking
            for (const item of cartItems) {
                const result = await bookingService.createBooking({
                    payment_reference: reference.reference,
                    service_type: item.type,
                    amount: item.price,
                    search_id: item.search_id,
                    offer_id: item.offer_id,
                    passengers: passengers,
                    contact_email: contactEmail,
                    contact_phone: contactPhone,
                    booking_details: {
                        ...item.details,
                        session_id: item.session_id,
                    }
                });

                setBookingResult(result);
            }

            setStatus('success');
            clearCart();
        } catch (error: any) {
            console.error('Booking creation error:', error);
            setErrorMessage(error.message || 'Booking creation failed. Your payment was received — please contact support.');
            setStatus('error');
        }
    };

    const onClose = () => {
        if (status === 'idle') {
            console.log('Payment dialog closed without paying');
        }
    };

    const initializePayment = usePaystackPayment(config);

    if (!passengers) return null;

    return (
        <>
            <Header />
            <div className="min-h-screen pt-24 pb-12 px-4 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #1a2d50 100%)' }}>

                {/* ─── Success State ─── */}
                {status === 'success' && (
                    <div className="rounded-2xl p-10 max-w-lg w-full text-center" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                        <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: 'rgba(34,197,94,0.15)' }}>
                            <i className="ri-check-double-line text-4xl text-green-400"></i>
                        </div>
                        <h1 className="text-2xl font-bold text-white mb-2">Booking Confirmed!</h1>
                        <p className="text-slate-400 mb-2">{bookingResult?.message || 'Your booking has been successfully processed.'}</p>
                        {bookingResult?.pnr && (
                            <div className="my-4 px-4 py-3 rounded-xl" style={{ background: 'rgba(212,168,83,0.15)', border: '1px solid rgba(212,168,83,0.3)' }}>
                                <p className="text-sm text-slate-400">Booking Reference (PNR)</p>
                                <p className="text-2xl font-mono font-bold text-amber-400">{bookingResult.pnr}</p>
                            </div>
                        )}
                        {bookingResult?.booking_id && (
                            <p className="text-xs text-slate-500 mb-4">Booking ID: {bookingResult.booking_id}</p>
                        )}
                        <p className="text-slate-400 text-sm mb-6">A confirmation email will be sent to <span className="text-white font-medium">{contactEmail}</span></p>
                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => navigate('/booking/my-bookings?tab=tickets')}
                                className="px-6 py-3 rounded-xl font-semibold text-white transition-all"
                                style={{ background: 'linear-gradient(135deg, #d4a853 0%, #b8902e 100%)' }}
                            >
                                View My Tickets
                            </button>
                            <button
                                onClick={() => navigate('/')}
                                className="px-6 py-3 rounded-xl font-semibold text-slate-300 hover:text-white transition-colors"
                                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                            >
                                Go Home
                            </button>
                        </div>
                    </div>
                )}

                {/* ─── Error State ─── */}
                {status === 'error' && (
                    <div className="rounded-2xl p-10 max-w-lg w-full text-center" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                        <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: 'rgba(239,68,68,0.15)' }}>
                            <i className="ri-error-warning-line text-4xl text-red-400"></i>
                        </div>
                        <h1 className="text-2xl font-bold text-white mb-2">Booking Issue</h1>
                        <p className="text-slate-400 mb-6">{errorMessage}</p>
                        <button
                            onClick={() => navigate('/booking/my-bookings')}
                            className="px-6 py-3 rounded-xl font-semibold text-white transition-all"
                            style={{ background: 'linear-gradient(135deg, #d4a853 0%, #b8902e 100%)' }}
                        >
                            Check Bookings
                        </button>
                    </div>
                )}

                {/* ─── Processing / Confirming State ─── */}
                {(status === 'processing' || status === 'confirming') && (
                    <div className="rounded-2xl p-10 max-w-lg w-full text-center" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                        <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse" style={{ background: 'rgba(212,168,83,0.15)' }}>
                            <i className="ri-loader-4-line text-4xl text-amber-400 animate-spin"></i>
                        </div>
                        <h1 className="text-2xl font-bold text-white mb-2">
                            {status === 'confirming' ? 'Securing Your Booking...' : 'Processing Payment...'}
                        </h1>
                        <p className="text-slate-400">
                            {status === 'confirming'
                                ? 'We are confirming your reservation with the airline. Please wait...'
                                : 'Please complete the payment in the popup window.'}
                        </p>
                    </div>
                )}

                {/* ─── Idle: Ready to Pay ─── */}
                {status === 'idle' && (
                    <div className="rounded-2xl p-8 max-w-md w-full text-center" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: 'rgba(212,168,83,0.15)' }}>
                            <i className="ri-secure-payment-line text-3xl text-amber-400"></i>
                        </div>
                        <h1 className="text-2xl font-bold text-white mb-2">Confirm Payment</h1>
                        <p className="text-slate-400 mb-1">You are about to pay</p>
                        <p className="text-3xl font-bold text-amber-400 mb-6">{formatPrice(totalAmount)}</p>

                        {/* Order summary mini */}
                        <div className="mb-6 rounded-xl p-4 text-left" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                            {cartItems?.map((item: any, i: number) => (
                                <div key={i} className="flex justify-between items-center py-2" style={{ borderBottom: i < cartItems.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                                    <span className="text-slate-400 capitalize text-sm">
                                        <i className={`ri-${item.type === 'flight' ? 'flight-takeoff' : 'hotel'}-line mr-1 text-amber-400`}></i>
                                        {item.type}
                                    </span>
                                    <span className="text-white font-medium text-sm">{formatPrice(item.price)}</span>
                                </div>
                            ))}
                            <div className="flex justify-between items-center pt-2 mt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.15)' }}>
                                <span className="text-white font-bold">Total</span>
                                <span className="text-amber-400 font-bold">{formatPrice(totalAmount)}</span>
                            </div>
                        </div>

                        <p className="text-slate-500 text-xs mb-6">
                            <i className="ri-user-line mr-1"></i>
                            {passengers?.length} passenger{passengers?.length > 1 ? 's' : ''} · {contactEmail}
                        </p>

                        <div className="space-y-3">
                            <button
                                className="w-full py-3.5 rounded-xl font-bold text-white text-lg transition-all hover:shadow-lg hover:shadow-green-500/20"
                                style={{ background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' }}
                                onClick={() => {
                                    setStatus('processing');
                                    initializePayment({ onSuccess, onClose });
                                }}
                            >
                                <i className="ri-bank-card-line mr-2"></i>
                                Pay Now with Paystack
                            </button>
                            <button
                                className="w-full py-3 rounded-xl font-medium text-slate-400 hover:text-white transition-colors"
                                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                                onClick={() => navigate(-1)}
                            >
                                Cancel
                            </button>
                        </div>

                        <p className="mt-4 text-xs text-slate-500">
                            <i className="ri-lock-line mr-1"></i>
                            256-bit SSL encryption · Secured by Paystack
                        </p>
                    </div>
                )}
            </div>
            <Footer />
        </>
    );
}
